import React, { useEffect, useState } from "react";
import { message, Checkbox, Button, Modal, Spin } from "antd";
import { FaCheck } from "react-icons/fa6";
import { RxCross1 } from "react-icons/rx";
import ConsentServices from "../../Services/ConsentServices";

function ConsentPopup({ visible, onClose, patientData, existingConsentData }) {
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [consentData, setConsentData] = useState({
    terms: "P",
    privacy: "P",
    doctors: "P",
    hospitals: "P",
    pharmacy: "P",
    diagnostics: "P",
  });

  useEffect(() => {
    if (visible && patientData?.patientId) {
      if (existingConsentData) {
        setConsentData({
          terms: existingConsentData.termsCond || "P",
          privacy: existingConsentData.privacyPolicy || "P",
          doctors: existingConsentData.shareMedWithDocs || "P",
          hospitals: existingConsentData.shareMedWithHosp || "P",
          pharmacy: existingConsentData.shareEpresWithPharmacy || "P",
          diagnostics: existingConsentData.shareEpresWithDiagnostics || "P",
        });
      } else {
        getConsent();
      }
    }
  }, [visible, patientData, existingConsentData]);

  const getConsent = async () => {
    setFetching(true);
    const payload = {
      patientId: patientData?.patientId,
    };
    try {
      const response = await ConsentServices.getconsentFilter(payload);
      if (response.data && response.data.length > 0) {
        const data = response.data[0];
        setConsentData({
          terms: data.termsCond,
          privacy: data.privacyPolicy,
          doctors: data.shareMedWithDocs,
          hospitals: data.shareMedWithHosp,
          pharmacy: data.shareEpresWithPharmacy,
          diagnostics: data.shareEpresWithDiagnostics,
        });
      }
    } catch (e) {
      console.error("Error fetching consent:", e);
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (type, value) => {
    setConsentData((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const now = new Date();
    const formattedTime =
      now.getFullYear() +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0") +
      " " +
      String(now.getHours()).padStart(2, "0") +
      ":" +
      String(now.getMinutes()).padStart(2, "0") +
      ":" +
      String(now.getSeconds()).padStart(2, "0");

    try {
      const postData = {
        account_Id: patientData?.account_Id,
        clinic_Id: patientData?.clinic_Id,
        patientId: patientData?.patientId,
        createdBy: patientData?.patientId,
        modifiedBy: patientData?.patientId,
        createdDate: formattedTime,
        modifiedDate: formattedTime,
        privacyPolicy: consentData.privacy,
        shareEpresWithDiagnostics: consentData.diagnostics,
        shareEpresWithPharmacy: consentData.pharmacy,
        shareMedWithDocs: consentData.doctors,
        shareMedWithHosp: consentData.hospitals,
        termsCond: consentData.terms,
        status: 1,
      };
      await ConsentServices.consentpost(postData);
      sessionStorage.setItem("consentChecked", "true");
      message.success("Consent submitted successfully!");
      onClose();
    } catch (error) {
      console.error("Error submitting data:", error);
      message.error("Failed to submit consent.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem("consentChecked", "true");
    onClose();
  };

  const consentItems = [
    { key: "terms", label: "Terms & Conditions" },
    { key: "privacy", label: "Privacy Policy" },
    { key: "doctors", label: "Share Medical Records with Doctors" },
    { key: "hospitals", label: "Share Medical Records with Hospitals" },
    { key: "pharmacy", label: "Share e-Prescription with Pharmacy" },
    { key: "diagnostics", label: "Share e-Prescription with Diagnostics" },
  ];

  const descriptions = {
    terms: "By using our services, you agree to comply with our terms and conditions.",
    privacy: "Your data is securely stored and only shared with authorized entities.",
    doctors: "Allow doctors to access your medical records for better diagnosis.",
    hospitals: "Allow hospitals to access your medical history for better treatment.",
    pharmacy: "Your prescriptions can be accessed by pharmacies for quick refills.",
    diagnostics: "Laboratories can use your e-prescriptions for accurate diagnostics.",
  };

  return (
    <Modal
      title={<div className="text-xl font-bold text-iconBlue">Update Your Consents</div>}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
      maskClosable={false}
      closable={false}
      className="consent-popup-modal"
      maskStyle={{ backdropFilter: "blur(12px)", backgroundColor: "rgba(255, 255, 255, 0.52)" }}
    >
      <div className="py-2">
        <p className="text-gray-600 mb-4 text-sm">
          To provide the best healthcare experience, please review and update your consent preferences.
        </p>
        
        {fetching ? (
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-3">
            {consentItems.map((item) => (
              <div
                key={item.key}
                className="border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setActiveModal(item.key)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-gray-700">{item.label}</span>
                  <div className="flex items-center gap-2">
                    {consentData[item.key] === "Y" ? (
                      <div className="bg-green-100 p-1 rounded-full">
                        <FaCheck className="text-green-600 text-xs" />
                      </div>
                    ) : consentData[item.key] === "N" ? (
                      <div className="bg-red-100 p-1 rounded-full">
                        <RxCross1 className="text-red-600 text-xs" />
                      </div>
                    ) : (
                      <span className="text-[10px] text-orange-500 font-bold uppercase tracking-wider">Pending</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Button onClick={handleSkip} className="border-gray-300 text-gray-600 hover:text-gray-800 rounded-lg h-10 px-6">
            Skip for now
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            className="bg-[#189FAA] hover:bg-[#158a94] border-none rounded-lg h-10 px-8 font-semibold"
          >
            Save Preferences
          </Button>
        </div>
      </div>

      {/* Internal Modal for Detail Descriptions */}
      <Modal
        title={activeModal ? consentItems.find(i => i.key === activeModal)?.label : ""}
        open={!!activeModal}
        onCancel={() => setActiveModal(null)}
        footer={[
          <Button key="disagree" danger onClick={() => { handleChange(activeModal, "N"); setActiveModal(null); }}>
            Disagree
          </Button>,
          <Button key="agree" type="primary" className="bg-green-600 border-none" onClick={() => { handleChange(activeModal, "Y"); setActiveModal(null); }}>
            I Agree
          </Button>
        ]}
        centered
        width={350}
        maskStyle={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(255, 255, 255, 0.5)" }}
      >
        <p className="text-gray-700 py-4">{activeModal && descriptions[activeModal]}</p>
      </Modal>
    </Modal>
  );
}

export default ConsentPopup;
